import React, { useRef, useEffect } from "react";
import { buildValueToLabel } from "./tacticModel";
import { queryClass, actionClass, branchTaken } from "./inspectorTrace";
import "./InspectorTactic.css";

// Read-only render of a tactic with the decision trace painted on top: each
// condition green/red/gray, the not-taken branch dimmed, the fired action bold.
// It walks the SAME statement tree the editor uses (deserializeStatements), so
// every node carries the tokenOffset the trace references. No editing affordances.

// Flatten a query into chips + operators, carrying each atom's tokenOffset so it
// can be colored individually (mirrors QueryDisplay's flattenQuery + offsets).
function flattenQuery(query, syntaxByValue) {
  if (!query) return [];
  switch (query.kind) {
    case "atom":
      return [{ type: "atom", value: query.value, arg: query.arg ?? null, offset: query.tokenOffset }];
    case "not":
      return [{ type: "op", label: syntaxByValue[8] ?? "NOT" }, ...flattenQuery(query.operand, syntaxByValue)];
    case "and":
      return [
        ...flattenQuery(query.left, syntaxByValue),
        { type: "op", label: syntaxByValue[6] ?? "AND" },
        ...flattenQuery(query.right, syntaxByValue),
      ];
    case "or":
      return [
        ...flattenQuery(query.left, syntaxByValue),
        { type: "op", label: syntaxByValue[7] ?? "OR" },
        ...flattenQuery(query.right, syntaxByValue),
      ];
    default:
      return [];
  }
}

function Condition({ query, tokens, state }) {
  const syntaxByValue = buildValueToLabel(tokens.syntax ?? {});
  const queryByValue = buildValueToLabel(tokens.queries ?? {});
  const zoneByValue = buildValueToLabel(tokens.zones ?? {});
  const items = flattenQuery(query, syntaxByValue);
  // overall condition mark comes from the top query node's own offset
  const overall = state.has ? queryClass(query?.tokenOffset, state) : "insp-gray";

  return (
    <span className={`insp-cond ${overall}`}>
      {items.map((it, i) =>
        it.type === "op" ? (
          <span key={i} className="insp-op">{it.label}</span>
        ) : (
          <span key={i} data-offset={it.offset} className={`insp-chip ${state.has ? queryClass(it.offset, state) : "insp-gray"}`}>
            {queryByValue[it.value] ?? it.value}
            {it.arg != null && <span className="insp-arg">({zoneByValue[it.arg] ?? it.arg})</span>}
          </span>
        )
      )}
    </span>
  );
}

function Statement({ statement, tokens, state, depth }) {
  const syntaxByValue = buildValueToLabel(tokens.syntax ?? {});
  const actionByValue = buildValueToLabel(tokens.actions ?? {});
  const zoneByValue = buildValueToLabel(tokens.zones ?? {});
  const CALL_ACTION = 1030; // must match tokens.hh — a call renders as @calleeName
  const renderAction = (actionVal, arg, callTarget) => {
    if (actionVal === CALL_ACTION) {
      const name = tokens.callables?.find((c) => c.id === callTarget)?.name ?? callTarget;
      return <>{name}</>;
    }
    return (
      <>
        {actionByValue[actionVal] ?? actionVal}
        {arg != null && <span className="insp-arg">({zoneByValue[arg] ?? arg})</span>}
      </>
    );
  };
  // Paint class for an action. A CALL line is emphasized (fired) once the
  // decision stepped into its target tactic, so a learner sees the jump to @T.
  const actClass = (off, actionVal, callTarget) =>
    (actionVal === CALL_ACTION && state.calls?.has(callTarget)) ? "insp-fired" : actionClass(off, state);
  const kwIf = syntaxByValue[4] ?? "IF";
  const kwElse = syntaxByValue[5] ?? "ELSE";
  const indent = { paddingLeft: `${depth * 1.1}rem` };

  switch (statement.kind) {
    case "action":
      return (
        <div className="insp-row" style={indent}>
          <span className="insp-arrow">→</span>
          <span data-offset={statement.tokenOffset} className={`insp-act ${actClass(statement.tokenOffset, statement.action, statement.callTarget)}`}>
            {renderAction(statement.action, statement.arg, statement.callTarget)}
          </span>
        </div>
      );

    case "conditional_action":
      return (
        <div className="insp-row" style={indent}>
          <span className="insp-kw">{kwIf}</span>
          <span className="insp-paren">(</span>
          <Condition query={statement.condition} tokens={tokens} state={state} />
          <span className="insp-paren">)</span>
          <span className="insp-arrow">→</span>
          <span data-offset={statement.action?.tokenOffset ?? -1} className={`insp-act ${actClass(statement.action?.tokenOffset ?? -1, statement.action, statement.callTarget)}`}>
            {renderAction(statement.action, statement.arg, statement.callTarget)}
          </span>
        </div>
      );

    case "if": {
      const taken = branchTaken(statement.condition, state);
      return (
        <div className="insp-if">
          <div className="insp-row" style={indent}>
            <span className="insp-kw">{statement._elseIf ? `${kwElse} ${kwIf}` : kwIf}</span>
            <span className="insp-paren">(</span>
            <Condition query={statement.condition} tokens={tokens} state={state} />
            <span className="insp-paren">)</span>
            <span className="insp-brace">{"{"}</span>
          </div>
          <div className={taken === "else" ? "insp-dim" : ""}>
            <StatementList statements={statement.then} tokens={tokens} state={state} depth={depth + 1} />
          </div>
          <div className="insp-brace-close" style={indent}>{"}"}</div>

          {statement.else && statement.else.length === 1 && statement.else[0].kind === "if" ? (
            <div className={taken === "then" ? "insp-dim" : ""}>
              <Statement statement={{ ...statement.else[0], _elseIf: true }} tokens={tokens} state={state} depth={depth} />
            </div>
          ) : statement.else ? (
            <>
              <div className="insp-row" style={indent}>
                <span className="insp-kw">{kwElse}</span>
                <span className="insp-brace">{"{"}</span>
              </div>
              <div className={taken === "then" ? "insp-dim" : ""}>
                <StatementList statements={statement.else} tokens={tokens} state={state} depth={depth + 1} />
              </div>
              <div className="insp-brace-close" style={indent}>{"}"}</div>
            </>
          ) : null}
        </div>
      );
    }

    case "block":
      return (
        <div className="insp-block">
          <div className="insp-row" style={indent}><span className="insp-brace">{"{"}</span></div>
          <StatementList statements={statement.statements} tokens={tokens} state={state} depth={depth + 1} />
          <div className="insp-brace-close" style={indent}>{"}"}</div>
        </div>
      );

    default:
      return null;
  }
}

function StatementList({ statements, tokens, state, depth }) {
  return (
    <div className="insp-list">
      {(statements ?? []).map((s, i) => (
        <Statement key={i} statement={s} tokens={tokens} state={state} depth={depth} />
      ))}
    </div>
  );
}

/**
 * @param {{ statements: object[], tokens: object, state: object }} props
 *   statements: deserialized tactic tree (nodes carry tokenOffset)
 *   tokens:     { queries, actions, syntax, zones } from useTokens()
 *   state:      decisionState(...) for the selected player at the current frame
 */
// The "active" line to keep in view: the fired action if any, otherwise the most
// recently revealed condition (last inserted in the visited map during narration).
function activeOffset(state) {
  if (!state) return null;
  if (state.fired != null && state.fired !== -1) return state.fired;
  let last = null;
  for (const off of state.visited.keys()) last = off; // Map preserves reveal order
  return last;
}

export function InspectorTactic({ statements, tokens, state }) {
  const scrollRef = useRef(null);
  const active = activeOffset(state);

  // Keep the running/fired line visible as the trace advances.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || active == null) return;
    const el = root.querySelector(`[data-offset="${active}"]`);
    // guarded: jsdom (tests) has no real scrollIntoView
    if (el && typeof el.scrollIntoView === "function") {
      try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* no-op */ }
    }
  }, [active]);

  if (!tokens || !statements) return null;
  return (
    <div className="insp-tactic" ref={scrollRef}>
      <StatementList statements={statements} tokens={tokens} state={state} depth={0} />
    </div>
  );
}
